import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Stack, Text, useViewport } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { trackWithDefaultStage } from "@flights/et-universal";
import { useFormatDateTime } from "@bookingcom/flights-core";

import Container from "components/elements/Container";
import GeniusAirlinesBanner from "components/elements/GeniusAirlinesBanner";
import DealsBanner from "components/elements/DealsBanner";
import { mcn } from "utils/mergeClassnames";
import { SUMMER_DEAL_BANNER_IMAGE_URL } from "../../../constants";

import styles from "./HomepageOffers.module.css";
import { getIndexCampaigns } from "store/indexCampaigns/selectors";
import useHomeRecentSearch from "components/elements/HomeRecentSearch/useHomeRecentSearch.controller";

const HomepageOffers = ({ isMobile }: { isMobile: boolean }) => {
  const { isLarge } = useViewport();
  const geniusAirlineData = useSelector(getIndexCampaigns).geniusAirlines;
  const i18n = useI18n();
  const { formats } = useFormatDateTime(i18n);
  const { searchHistoryList } = useHomeRecentSearch();

  const hasSearchHistory = searchHistoryList.length !== 0 && !isMobile;

  const geniusStyles = mcn(
    isLarge ? styles.item : styles.itemNoWidth,
    trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 1 && styles.itemSingle
  );

  const showFirstBanner = useMemo(() => {
    if (isMobile) {
      return true;
    }

    return trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 2;
  }, [isMobile]);

  const showSecondBanner = useMemo(() => {
    if (isMobile) {
      return geniusAirlineData && trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 2;
    }

    return geniusAirlineData && !!trackWithDefaultStage("flights_web_cat_summer_deal", 1);
  }, [geniusAirlineData, isMobile]);

  return (
    <Container
      pt={trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 2 || isMobile || hasSearchHistory ? 6 : 0}
      className={isMobile ? styles.mobileContainer : styles.container}
    >
      {isMobile && trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 1 ? null : (
        <Stack direction="column" gap={0} className={styles.top}>
          <Text variant="headline_2">{i18n.trans(t("flights_index_offers_carousel_header"))}</Text>
          <Text variant="body_1" color="neutral_alt">
            {i18n.trans(t("flights_index_offers_carousel_subheader"))}
          </Text>
        </Stack>
      )}
      <Stack direction={!isLarge ? "column" : "row"} alignItems="center" justifyContent="center" gap={4} wrap="wrap">
        {showFirstBanner ? (
          <Stack.Item className={isLarge && geniusAirlineData ? styles.item : styles.itemNoWidth}>
            <DealsBanner
              title={i18n.trans(t("flights_cat_summer_deal_sr_header"))}
              subtitle={i18n.trans(
                t("flights_cat_summer_deal_sr_subheader", {
                  variables: {
                    end_date: formats.flightDate(new Date("2025-06-26"))
                  }
                })
              )}
              imageUrl={SUMMER_DEAL_BANNER_IMAGE_URL}
              showSecondBanner={showSecondBanner}
            />
          </Stack.Item>
        ) : null}
        {showSecondBanner ? (
          <Stack.Item className={geniusStyles}>
            <GeniusAirlinesBanner showText={trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 1} />
          </Stack.Item>
        ) : null}
      </Stack>
    </Container>
  );
};

export default HomepageOffers;
